/**
 * Toast Notification Utility
 *
 * react-toastify를 감싸는 포터블 래퍼입니다.
 * DI(의존성 주입) 패턴을 사용하여 프로젝트 독립적으로 설계되었습니다.
 *
 * 사용법:
 * ```javascript
 * import { ToastService, defaultToast } from './toast';
 *
 * // 기본 인스턴스 사용
 * defaultToast.success('성공!');
 * defaultToast.error('오류 발생');
 * defaultToast.warning('경고');
 * defaultToast.info('정보');
 *
 * // 커스텀 인스턴스 생성
 * const customToast = new ToastService({
 *   defaultOptions: {
 *     autoClose: 5000,
 *     position: 'bottom-right'
 *   }
 * });
 * ```
 */

/**
 * ToastService 클래스
 * 의존성 주입을 통해 toast 라이브러리를 커스터마이징 가능
 */
export class ToastService {
  /**
   * @param {Object} options - 설정 옵션
   * @param {Object} options.toastLibrary - 토스트 라이브러리 (기본값: react-toastify)
   * @param {Object} options.defaultOptions - 기본 토스트 옵션
   */
  constructor(options = {}) {
    // 토스트 라이브러리 주입 (기본값: react-toastify)
    this.toastLibrary = options.toastLibrary || null;

    // 기본 토스트 옵션
    this.defaultOptions = {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options.defaultOptions,
    };

    // Lazy load react-toastify (프로젝트 독립성 유지)
    if (!this.toastLibrary) {
      try {
        // 동적 import는 실제 사용 시점에 수행
        this.toastLibrary = require('react-toastify').toast;
      } catch (error) {
        console.warn('react-toastify not installed, falling back to console');
        // Fallback: console 사용
        this.toastLibrary = {
          success: (msg) => console.log('[SUCCESS]', msg),
          error: (msg) => console.error('[ERROR]', msg),
          warning: (msg) => console.warn('[WARNING]', msg),
          info: (msg) => console.info('[INFO]', msg),
        };
      }
    }
  }

  /**
   * 성공 메시지 표시
   * @param {string} message - 표시할 메시지
   * @param {Object} options - 토스트 옵션 (defaultOptions 오버라이드)
   * @returns {string|number} 토스트 ID
   */
  success(message, options = {}) {
    return this.toastLibrary.success(message, {
      ...this.defaultOptions,
      ...options,
    });
  }

  /**
   * 에러 메시지 표시
   * @param {string} message - 표시할 메시지
   * @param {Object} options - 토스트 옵션 (defaultOptions 오버라이드)
   * @returns {string|number} 토스트 ID
   */
  error(message, options = {}) {
    return this.toastLibrary.error(message, {
      ...this.defaultOptions,
      autoClose: 5000, // 에러는 조금 더 길게
      ...options,
    });
  }

  /**
   * 경고 메시지 표시
   * @param {string} message - 표시할 메시지
   * @param {Object} options - 토스트 옵션 (defaultOptions 오버라이드)
   * @returns {string|number} 토스트 ID
   */
  warning(message, options = {}) {
    return this.toastLibrary.warning(message, {
      ...this.defaultOptions,
      ...options,
    });
  }

  /**
   * 정보 메시지 표시
   * @param {string} message - 표시할 메시지
   * @param {Object} options - 토스트 옵션 (defaultOptions 오버라이드)
   * @returns {string|number} 토스트 ID
   */
  info(message, options = {}) {
    return this.toastLibrary.info(message, {
      ...this.defaultOptions,
      ...options,
    });
  }
}

/**
 * 기본 인스턴스 (싱글톤)
 *
 * 대부분의 경우 이 인스턴스를 사용하면 됩니다.
 */
export const defaultToast = new ToastService();
